import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { hasPermission } from "../../utils/permissions";
import { usePlan } from "../../contexts/PlanContext";

// No stylesheet — converted to Tailwind utilities in Phase 5.
// lead-capture-banner.scss was deleted in the same commit (TOKEN_CONTRACT §6.1).
//
// It carried THREE dark-mode rules to swap one icon colour: a
// prefers-color-scheme media query, a :root:not([data-theme="light"]) guard, and a
// :root[data-theme="dark"] selector — 6 lines and two hardcoded ambers to say
// "this icon is lighter in dark mode". --warning does that on its own.

/**
 * Warns, on the Enquiries screen, when Facebook lead capture has stopped.
 *
 * Settings → Lead Sources already shows this, but nobody opens Settings to
 * discover a problem — they notice on **this** page, days later, that the
 * queue is thin. A permission removed in Meta Business Settings produces no
 * error anywhere; the webhook stays subscribed, the stored scope list still
 * looks right, and leads simply stop.
 *
 * So the warning goes where the absence is felt.
 *
 * Deliberately quiet when healthy and quiet when Meta is unreachable
 * (`state: 'unknown'`) — a banner that appears because our egress had a bad
 * minute is a banner people learn to ignore.
 */
const LeadCaptureBanner = () => {
  const { hasPlanFeature } = usePlan();
  const [health, setHealth] = useState(null);
  // Lead sync is a plan feature of its own, separate from ads publishing. Off
  // the plan there is no connection to be broken, and the health endpoint that
  // would answer is itself gated — so this asks nothing.
  const planAllows = hasPlanFeature("leadCapture");

  useEffect(() => {
    if (!planAllows) return;
    // Only admins who can act on it should see it. A counsellor cannot
    // reconnect Facebook, so for them it is noise on a screen they live in.
    if (!hasPermission("add_edit_enquiries")) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/lead-sources/meta-ads/health");
        if (!cancelled) setHealth(data);
      } catch {
        // Advisory only — never let this break the Enquiries page.
      }
    })();
    return () => { cancelled = true; };
  }, [planAllows]);

  /**
   * `not_connected` with no Pages ever connected is the normal state for an
   * institute that does not use Meta Lead Ads at all — warning them would be
   * nagging about a feature they never turned on. Only warn when something that
   * *was* working has broken: the grant lost scopes, or Pages exist and the
   * connection has since died.
   */
  const lostScopes = ["degraded", "needs_pages"].includes(health?.state);
  const diedWithPages = health?.state === "not_connected" && health?.activePages > 0;

  if (!lostScopes && !diedWithPages) return null;

  return (
    <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-warning/45 tw-bg-warning/10 tw-px-3.5 tw-py-2.5 tw-text-[0.8125rem] tw-leading-snug tw-text-content [&_strong]:tw-font-semibold">
      <AlertTriangle size={15} className="tw-shrink-0 tw-text-warning-fg" />
      <div className="tw-min-w-0 tw-flex-1">
        <strong>Facebook lead capture has stopped.</strong>{" "}
        {health.missingLabels?.length > 0
          ? `Your Facebook connection no longer allows: ${health.missingLabels.join(", ")}.`
          : "The connection to Facebook is no longer valid."}{" "}
        New leads from Facebook and Instagram forms are not reaching this list.
      </div>
      <Link
        to="/settings?tab=lead-sources"
        className="tw-shrink-0 tw-whitespace-nowrap tw-rounded-md tw-bg-primary-solid tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-primary-solid-fg tw-no-underline hover:tw-text-primary-solid-fg hover:tw-opacity-90"
      >
        Reconnect
      </Link>
    </div>
  );
};

export default LeadCaptureBanner;
